import React, {useState, Fragment, useContext, useRef, useCallback, useMemo} from 'react';
import { Button, Card, Accordion, AccordionContext } from "react-bootstrap";
import { useAccordionToggle } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { onEnterOrSpace } from '../../../helpers/keyboardA11y';
import LavatoryIcon from "../../../assets/images/Web_Lavatory_Icon.svg";
import StatusInactiveIcon from "../../../assets/images/Web_Lavatory_Inactive_Icon.svg";
import { Player } from '@lottiefiles/react-lottie-player';
import DetailsComponent from "../../../modules/details/DetailsComponent";
import ArrivalFlightIcon from "../../../assets/images/Web_FlightArrival_Icon.svg";
import DropdownIcon from "../../../assets/images/Web_Dropdown_Icon.svg";
import DropdownExpandIcon from "../../../assets/images/Web_Dropdown_Expand_Icon.svg";
import { TourSteps } from './tourSteps'
import UnknowledgeIcon from "../../../assets/images/Web_Unknowledge_Icon.svg";
import timer1 from '../../../assets/images/Web_Timer01_Icon.svg';
import timer2 from '../../../assets/images/Web_Timer02_Icon.svg';
import timer3 from '../../../assets/images/Web_Timer03_Icon.svg';
import timer4 from '../../../assets/images/Web_Timer04_Icon.svg';
import timer5 from '../../../assets/images/Web_Timer05_Icon.svg';
import CancelIcon from "../../../assets/images/Web_Cancel_Icon.svg";
import CompleteIcon from "../../../assets/images/Web_Complete_Icon.svg";
import { SquenceAnimator }  from 'react-sequence-animator';

// Memoized DetailToggleButton component to prevent unnecessary re-renders
const DetailToggleButton = React.memo(({ eventKey, handleChange, isOpen, currentTourStep, nextTourStep }) => {
    const currentEventKey = useContext(AccordionContext);
   
    const decoratedOnClick = useAccordionToggle(
      eventKey,
      useCallback(() => {
        if (currentTourStep === 6 || currentTourStep === 7) {
            nextTourStep();
            if(isOpen === 'open') {
                handleChange(null);
            } else {
                handleChange('open');
            }
        } else {
            return;
        }
      }, [currentTourStep, nextTourStep, isOpen, handleChange])
    );
    
    const isCurrentEventKey = currentEventKey === eventKey;

    return (
      <button
        type="button"
        className="TaskToggleButton expandTour"
        onClick={decoratedOnClick}
      >
        <img
          src={isCurrentEventKey ? DropdownExpandIcon : DropdownIcon}
          alt="Toggle accordion"
        />
      </button>
    );
});

DetailToggleButton.displayName = 'DetailToggleButton';

DetailToggleButton.propTypes = {
    eventKey: PropTypes.string.isRequired,
    handleChange: PropTypes.func.isRequired,
    isOpen: PropTypes.string,
    currentTourStep: PropTypes.number.isRequired,
    nextTourStep: PropTypes.func.isRequired
};

// --- Extracted sub-component (ENG-3.1: reduce cognitive complexity) ---

const TapHandPlayer = () => (
    <Player 
        src={require('../../../assets/animations/tapHand.json')}
        background="transparent"  
        speed="1"
        style={{ marginLeft: 5, marginTop: 5, width: 80, height: 80, position: 'absolute' }}
        autoplay 
        loop
    />
);

const TourActionCells = React.memo(({ currentTourStep, taskState, handActionButton, handleActionButton, 
    actionButtonStyle, lavatorButtonStyle, tapHandActionStyle, toggleButtonStyle, isOpen, nextTourStep, 
    handleAccordionChange, getBlur }) => {
    const stepState = TourSteps[currentTourStep].state;
    const isAssigned = stepState === 'ASSIGNED';
    const isHandAction = handActionButton && handActionButton.includes(currentTourStep);
    const showToggleHand = handActionButton && !isHandAction && !TourSteps[currentTourStep].modal && !isAssigned;

    return (
        <>
            <td style={actionButtonStyle}>
                <button className="lavatorButton" onClick={handleActionButton} style={lavatorButtonStyle} id="actionButton">
                    {isHandAction && !isAssigned && <TapHandPlayer />}
                    {!isAssigned && (
                        <img src={taskState[stepState]?.icon || null} alt="Task status icon" />
                    )}
                </button>
            </td>
            <td style={toggleButtonStyle} colSpan="6" className='tdTour'>
                <div style={{ position: 'relative' }}>
                    <Accordion className='toggleButtonTour'>
                        <DetailToggleButton
                            eventKey={'open'}
                            handleChange={handleAccordionChange}
                            isOpen={isOpen}
                            nextTourStep={nextTourStep}
                            currentTourStep={currentTourStep}
                        />
                    </Accordion>
                    {showToggleHand && (
                        <Player 
                            src={require('../../../assets/animations/tapHand.json')}
                            background="transparent"  
                            speed="1"
                            className='toggleHandAnimationTour'
                            autoplay 
                            loop 
                        />
                    )}
                </div>
            </td>
        </>
    );
});

const TourModeTask = React.memo((props) => {
    const {
        currentTourStep,
        handActionButton,
        isOpen,
        setIsOpen,
        nextTourStep,
        previousTourStep,
    } = props;
    
    const [showTimer, toggleTimer] = useState(false);
    const timerSequence = useRef();

    // Memoized task state configuration
    const taskState = useMemo(() => ({
        'CONFIRMED': { background: '#ff9800', icon: StatusInactiveIcon },
        'STARTED': { background: '#14e715', icon: LavatoryIcon },
    }), []);

    // Memoized date calculation
    const currentDate = useMemo(() => new Date(), []);

    // Memoized timer handlers
    const stopTimer = useCallback(() => {
        previousTourStep();
        timerSequence.current.reset();
        toggleTimer(!showTimer);
    }, [previousTourStep, showTimer]);

    const sequenceCompleted = useCallback(() => {
        nextTourStep();
        toggleTimer(!showTimer);
    }, [nextTourStep, showTimer]);

    // Memoized blur calculation
    const getBlur = useCallback(() => {
        if (TourSteps[currentTourStep].state === 'ASSIGNED' || currentTourStep === 10 || currentTourStep === 11) {
            return 'blur(3px)';
        }
        return '';
    }, [currentTourStep]);

    // Memoized accordion change handler
    const handleAccordionChange = useCallback((state) => {
        setIsOpen(state);
    }, [setIsOpen]);

    // Memoized action button handler
    const handleActionButton = useCallback(() => {
        if (handActionButton && handActionButton.includes(currentTourStep)) {
            nextTourStep();
        }
    }, [handActionButton, currentTourStep, nextTourStep]);

    // Memoized styles
    const containerStyle = useMemo(() => ({
        alignSelf: "flex-start",
        paddingLeft: 0,
        paddingRight: 0
    }), []);

    const accordionStyle = useMemo(() => ({
        border: '1px solid #c7c7c7'
    }), []);

    const taskRowStyle = useMemo(() => {
        return TourSteps[currentTourStep].state === 'ASSIGNED' 
            ? { background: 'rgb(0 0 0 / 46%)' } 
            : { background: '' };
    }, [currentTourStep]);

    const taskItemStyle = useMemo(() => ({
        filter: getBlur(),
        display: 'flex',
        padding: '1px',
        paddingBottom: '0px',
        alignItems: 'stretch',
        textAlign: 'center'
    }), [getBlur]);

    const taskStripStyle = useMemo(() => {
        const state = TourSteps[currentTourStep].state;
        return taskState[state] ? {
            backgroundColor: taskState[state].background,
            color: taskState[state].background
        } : null;
    }, [currentTourStep, taskState]);

    const taskContentStyle = useMemo(() => ({
        alignSelf: 'center',
        paddingLeft: '2%',
        width: '100%',
        margin: '10px'
    }), []);

    const flightInfoStyle = useMemo(() => ({
        filter: getBlur(),
        fontSize: '16px',
        color: '#36495A',
        verticalAlign: 'middle',
        paddingLeft: '40px',
        paddingRight: '40px'
    }), [getBlur]);

    const locationStyle = useMemo(() => ({
        filter: getBlur(),
        alignSelf: 'center',
        paddingLeft: '40px',
        paddingRight: '40px',
        textAlign: 'center'
    }), [getBlur]);

    const aircraftStyle = useMemo(() => ({
        filter: getBlur(),
        paddingLeft: '40px',
        paddingRight: '40px'
    }), [getBlur]);

    const timeStyle = useMemo(() => ({
        paddingLeft: '40px',
        paddingRight: '40px'
    }), []);

    const taskAddedOverlayStyle = useMemo(() => ({
        position: 'absolute',
        zIndex: 5,
        display: 'flex',
        alignItems: 'start-flex'
    }), []);

    const taskAddedTextStyle = useMemo(() => ({
        fontWeight: '700',
        fontSize: 'Larger',
        color: 'white'
    }), []);

    const unknowledgeIconStyle = useMemo(() => ({
        marginLeft: '20px',
        position: 'relative',
        bottom: '30px'
    }), []);

    const tapHandStyle = useMemo(() => ({
        marginLeft: 5,
        marginTop: 5,
        width: 80,
        height: 80,
        position: 'relative',
        left: '-58px'
    }), []);

    const actionButtonStyle = useMemo(() => ({
        filter: getBlur(),
        paddingTop: '20px',
        verticalAlign: 'middle',
        width: '7%'
    }), [getBlur]);

    const lavatorButtonStyle = useMemo(() => ({
        margin: '10px',
        verticalAlign: 'middle'
    }), []);

    const tapHandActionStyle = useMemo(() => ({
        marginLeft: 5,
        marginTop: 5,
        width: 80,
        height: 80,
        position: 'absolute'
    }), []);

    const toggleButtonStyle = useMemo(() => ({
        filter: getBlur(),
        alignSelf: 'center',
        paddingLeft: '40px', 
        paddingRight: '40px', 
        textAlign: 'center'
    }), [getBlur]);

    const expandTapHandStyle = useMemo(() => ({
        marginLeft: 5,
        marginTop: 5,
        width: 80,
        height: 80,
        position: 'absolute',
        top: '385px'
    }), []);

    const completeButtonContStyle = useMemo(() => ({
        zIndex: 5,
        marginTop: '32px'
    }), []);

    // Memoized crew list data
    const csCrewListData = useMemo(() => [{
        firstName: 'Crew',
        lastName: 'Crew',
        station: 'DFW',
        userId: 'h',
        ackRequired: false,
        startTime: `${currentDate.getHours()}:${currentDate.getMinutes()}`,
        endTime: `${currentDate.getHours()}:${currentDate.getMinutes() + 60}`,
        taskStatus: 'CONFIRMED',
        tlinkRoute: false,
        taskName: 'D1',
    }], [currentDate]);

    const fsCrewListData = useMemo(() => [{
        firstName: 'Crew',
        lastName: 'Crew',
        station: 'DFW',
        userId: 'h',
        ackRequired: false,
        startTime: `${currentDate.getHours()}:${currentDate.getMinutes()}`,
        endTime: `${currentDate.getHours()}:${currentDate.getMinutes() + 60}`,
        taskStatus: 'STARTED',
        tlinkRoute: false,
        taskName: 'D1',
    }], [currentDate]);

    return (
        <div>
            <div className="container d-flex justify-content-between" style={containerStyle}>
                <h6 className='titleTaskTour'>Task Information - 1 Tasks</h6>
            </div>
            <div className='accordion' style={accordionStyle}>
                <thead className="TaskHeader tourHeader">
                    <tr>
                        <th>Task</th>
                        <th>Arrival/Departure</th>
                        <th>Location</th>
                        <th>Aircraft</th>
                        <th>Time</th>
                        <th colSpan="2">Status</th>
                    </tr>
                </thead>
                <tr key={1} style={taskRowStyle}>
                    <td style={taskItemStyle}>
                        {currentTourStep < 10 ? (
                            <div style={taskStripStyle} className='taskItemStrip'>
                                {' _'}
                            </div>
                        ) : null}
                        <div style={taskContentStyle}>
                            Tour Mode          
                        </div>
                    </td>
                    <td className='tdTour' style={flightInfoStyle}>
                        <div className="flight-info">
                            <figure>
                                <img src={ArrivalFlightIcon} alt="flight type icon" />
                                <div className="flight-city-diversion" style={{marginRight:'2px'}}>
                                </div> 
                                <div className="flight-city-diversion">
                                    A00
                                </div>                                  
                                <div className="flight-intl-flag">INTL</div>
                            </figure>
                            <aside>
                                <p>AA0000</p>
                                <p>IN {currentDate.getHours()}:{currentDate.getMinutes()} - {currentDate.getHours()}:{currentDate.getMinutes()}</p>
                            </aside>
                        </div>
                    </td>
                    <td className='tdTour' style={locationStyle}>
                        A00
                    </td>
                    <td className='tdTour' style={aircraftStyle}>
                        738K/3FD
                    </td>
                    <td className='tdTour' style={timeStyle}>
                        {TourSteps[currentTourStep].state === 'ASSIGNED' ? (
                            <div style={taskAddedOverlayStyle} role="button" tabIndex={0} onClick={nextTourStep} onKeyDown={onEnterOrSpace(nextTourStep)}>
                                <p style={taskAddedTextStyle}>Task Added</p>
                                <img src={UnknowledgeIcon} style={unknowledgeIconStyle} alt="Unknown task icon" />
                                <Player 
                                    src={require('../../../assets/animations/tapHand.json')}
                                    background="transparent"  
                                    speed="1"
                                    style={tapHandStyle}
                                    autoplay 
                                    loop
                                /> 
                            </div>
                        ) : null}
                        <p style={{filter: getBlur()}}>
                            {currentDate.getHours()}:{currentDate.getMinutes()}
                        </p>
                    </td>
                    {currentTourStep < 10 ? (
                        <TourActionCells 
                            currentTourStep={currentTourStep}
                            taskState={taskState}
                            handActionButton={handActionButton}
                            handleActionButton={handleActionButton}
                            actionButtonStyle={actionButtonStyle}
                            lavatorButtonStyle={lavatorButtonStyle}
                            tapHandActionStyle={tapHandActionStyle}
                            toggleButtonStyle={toggleButtonStyle}
                            isOpen={isOpen}
                            nextTourStep={nextTourStep}
                            handleAccordionChange={handleAccordionChange}
                            getBlur={getBlur}
                        />
                    ) : currentTourStep === 10 || currentTourStep === 11 ? (
                        <div className="completeButtonCont" style={completeButtonContStyle}>
                            <Button className="completeBtn" id="actionButton" onClick={previousTourStep}>
                                <img src={CancelIcon} alt="Cancel task" />
                            </Button>
                            <Button className="completeBtn" id="actionButton" onClick={nextTourStep}>
                                <img src={CompleteIcon} alt="Complete task" />
                            </Button>
                        </div>
                    ) : currentTourStep === 13 ? (
                        <div role="button" tabIndex={0} onClick={stopTimer} onKeyDown={onEnterOrSpace(stopTimer)}>
                            <SquenceAnimator duration={5000} loop={false} onSequenceEnd={sequenceCompleted} ref={timerSequence}>
                                <img src={timer5} alt="timer5"/>
                                <img src={timer4} alt="timer4"/>
                                <img src={timer3} alt="timer3"/>
                                <img src={timer2} alt="timer2"/>
                                <img src={timer1} alt="timer1"/>
                            </SquenceAnimator>
                        </div>  
                    ) : (
                        <></>
                    )}
                </tr>
                <Accordion.Collapse eventKey={isOpen}>
                    <Card.Body>
                        <Fragment>
                            <DetailsComponent
                                action={null}
                                commentsList={''}
                                decodedToken={{}}
                                empName={'Acknowledged'}
                                showNameFlag={false}
                                taskRoles={{}}
                                csCrewList={csCrewListData}
                                fsCrewList={fsCrewListData}
                                fetchDetailsParams={{}}
                                selectedTaskBar={{}}
                                callForDetailsAction={{}}
                                setRolesCount={0}
                                crewChief={false}
                                loginAsData={false}
                                callForAllUserDetails={() => null}
                                shiftInfoList={{}}
                                ccRoleEnabled={false}
                                allowedTaskRoles={[]}
                            />
                        </Fragment>
                    </Card.Body>    
                </Accordion.Collapse>
            </div>
        </div>
    );
});

TourModeTask.displayName = 'TourModeTask';

TourModeTask.propTypes = {
    currentTourStep: PropTypes.number.isRequired,
    handActionButton: PropTypes.array.isRequired,
    isOpen: PropTypes.string,
    setIsOpen: PropTypes.func.isRequired,
    nextTourStep: PropTypes.func.isRequired,
    previousTourStep: PropTypes.func.isRequired
};

export default TourModeTask;